import { type JSX, useId, useMemo, useRef, useState } from 'react';

import { type Locale, type Region, searchRegions, useI18n } from '../i18n';
import { useOverlay, useScrollLock } from './overlay';

/**
 * The region and language chooser.
 *
 * Region and language are chosen together because they are chosen together in
 * life — someone picking "United Kingdom" is answering both "where am I" and
 * "what do I read". Splitting them into two controls doubles the interaction
 * for a distinction most people never need to make.
 *
 * The list renders from the registry, so a new market is an entry in
 * `REGIONS`, not a change here. The search filters regions rather than
 * languages: with two entries it is barely needed, and with sixty it is the
 * only usable way in — building it now costs nothing and avoids the rewrite.
 */

export function LanguageModal({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}): JSX.Element | null {
  const { locale, setLocale, t } = useI18n();
  const [query, setQuery] = useState('');
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const titleId = useId();
  const searchId = useId();

  useOverlay({ open, onClose, containerRef: panelRef, initialFocusRef: searchRef });
  useScrollLock(open);

  const matches = useMemo(() => searchRegions(query), [query]);

  if (!open) return null;

  function choose(next: Locale): void {
    setLocale(next);
    onClose();
  }

  return (
    <div className="jt-modal" role="presentation">
      <div
        className="jt-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
      >
        <div className="jt-modal__head">
          <h2 className="jt-modal__title" id={titleId}>
            {t('locale.title')}
          </h2>
          <button type="button" className="jt-modal__close" onClick={onClose} aria-label={t('common.close')}>
            <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <label className="jt-visually-hidden" htmlFor={searchId}>
          {t('locale.searchLabel')}
        </label>
        <input
          id={searchId}
          ref={searchRef}
          type="search"
          className="jt-modal__search"
          placeholder={t('locale.searchPlaceholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        {matches.length === 0 ? (
          <p className="jt-modal__empty">{t('locale.noResults')}</p>
        ) : (
          <ul className="jt-modal__regions">
            {matches.map((region) => (
              <RegionRow key={region.code} region={region} current={locale} onChoose={choose} />
            ))}
          </ul>
        )}

        {/*
          Says what is actually translated. A language menu implies a set of
          languages, and the honest answer today is "one" — better stated than
          discovered by someone who picks a flag and gets English.
        */}
        <p className="jt-modal__note">{t('locale.note')}</p>
      </div>
    </div>
  );
}

function RegionRow({
  region,
  current,
  onChoose,
}: {
  readonly region: Region;
  readonly current: Locale;
  readonly onChoose: (locale: Locale) => void;
}): JSX.Element {
  const { t } = useI18n();

  return (
    <li className="jt-region">
      <p className="jt-region__name">
        <span className="jt-region__flag" aria-hidden="true">
          {region.flag}
        </span>
        {region.name}
      </p>
      <ul className="jt-region__languages">
        {region.languages.map((language) => {
          const selected = language.locale === current;
          return (
            <li key={language.locale}>
              <button
                type="button"
                className={`jt-region__language ${selected ? 'jt-region__language--on' : ''}`}
                // `aria-pressed` rather than `aria-current`: this is a toggle
                // of state, not a position in a set of pages.
                aria-pressed={selected}
                onClick={() => onChoose(language.locale)}
              >
                {language.endonym}
                {selected ? <span className="jt-visually-hidden"> ({t('locale.current')})</span> : null}
              </button>
            </li>
          );
        })}
      </ul>
    </li>
  );
}
